<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
              <template v-slot:button-content>
                <i class="mc-fa icon-outline_download"></i>{{ $t('global.action.download') }}
              </template>
              <b-dropdown-item href="#" @click="downloadReport('xlsx')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="columns.includes('address')"
      reportKey="door"
      v-if="Object.keys(filters).length > 0"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(doorOpenedOn)="data">
        <template v-if="data.item.doorOpenedOn">
          {{ momentDateShortMonth(data.item.doorOpenedOn) }}<br />
          <span class="text-muted">{{ momentTimeSeconds(data.item.doorOpenedOn, true) }}</span>
        </template>
        <span v-else>-</span>
      </template>
      <template #cell(doorClosedOn)="data">
        <template v-if="data.item.doorClosedOn">
          {{ momentDateShortMonth(data.item.doorClosedOn) }}<br />
          <span class="text-muted">{{ momentTimeSeconds(data.item.doorClosedOn, true) }}</span>
        </template>
        <span v-else>-</span>
      </template>
      <template #cell(driver)="data">
        {{ data.item.driver ? data.item.driver.fullname : '-' }}
      </template>
      <template #cell(vehicle)="data">
        {{ data.item.vehicle ? data.item.vehicle.licensePlate : '-' }}
      </template>

      <template #cell(vehicleType)="data">
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.manufacturer
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.model
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.type
              : '-'
            : '-'
        }}
      </template>

      <template #cell(address)="data">
        <b-skeleton width="70%" v-if="data.item.address == '' || data.item.address == null" />
        <AddressValue
          v-else
          :id="`address-${data.index}`"
          :latitude="data.item.latitude"
          :longitude="data.item.longitude"
          :address="data.item.address"
        />
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import { EXPORT_REPORT_DOOR_PAGINATED, EXPORT_DOOR_REPORT } from '@/services/api/report/report'
import McBadge from '@/components/McBadge'
import { MixPanel } from '@/mixin/mixpanel'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { mapGetters } from 'vuex'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import AddressValue from '../../components/AddressValue.vue'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    AddressValue,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'door',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
    }
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      this.reportFields.forEach((item) => {
        item.label = `${this.$t(item.label)}`
      })
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, ...otherFilter } = filters
        const startDate =
          periode.length > 0 ? moment(periode[0] + ' 00:00:00.000').toISOString() : null
        const endDate =
          periode.length > 0 ? moment(periode[1] + ' 23:59:59.000').toISOString() : null
        const model = {
          startDate,
          endDate,
          ...otherFilter,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.isBusy = true
          const res = await this.queryCache.fetch(
            EXPORT_REPORT_DOOR_PAGINATED,
            {
              filter: {
                ...model,
                column: this.columns.filter((item) => item != 'address'),
                ...filter,
              },
            },
            self.reportKey
          )
          const result = res.reportDoorPaginated.reportDoorPaginated
          self.totalRows = result?.pagination?.total ?? 0
          self.isBusy = false
          self.message = res.reportDoorPaginated.message
          self.isError = !res.reportDoorPaginated.isSuccessful
          self.modalVerifReport += 1
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
  },
  methods: {
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    redirectToMap(lat, long) {
      let url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
      window.open(url, '_blank')
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.doorReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        column: this.columns,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: EXPORT_DOOR_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.doorReport)
        })
    },
  },
}
</script>
